import jsPDF from "jspdf";

interface ActionItem {
  id: number;
  label: string;
  priority: string;
  category: string;
  done: boolean;
}

interface ActionPlanPdfOptions {
  projectName: string;
  actions: ActionItem[];
  doneCount: number;
  progress: number;
  headerImage?: string; // base64 data URL
}

// Colors
const AMBER = [200, 160, 50];
const DARK = [30, 30, 35];
const GREY = [100, 100, 110];
const LIGHT_GREY = [200, 200, 205];
const WHITE = [255, 255, 255];
const RED = [220, 60, 60];
const GREEN = [50, 180, 100];
const BLUE = [60, 120, 200];
const CARD_BG = [245, 245, 248];

function drawRoundedRect(doc: jsPDF, x: number, y: number, w: number, h: number, r: number, fill: number[], stroke?: number[]) {
  if (stroke) {
    doc.setDrawColor(stroke[0], stroke[1], stroke[2]);
    doc.setLineWidth(0.3);
  }
  doc.setFillColor(fill[0], fill[1], fill[2]);
  doc.roundedRect(x, y, w, h, r, r, stroke ? "FD" : "F");
}

function drawProgressBar(doc: jsPDF, x: number, y: number, w: number, h: number, pct: number) {
  drawRoundedRect(doc, x, y, w, h, h / 2, [230, 230, 235]);
  if (pct > 0) {
    const fillW = Math.max(h, w * (pct / 100));
    drawRoundedRect(doc, x, y, fillW, h, h / 2, AMBER);
  }
}

function drawDonutChart(doc: jsPDF, cx: number, cy: number, radius: number, pct: number) {
  const segments = 60;
  const filled = Math.round(segments * (pct / 100));
  doc.setDrawColor(230, 230, 235);
  doc.setLineWidth(4);
  doc.circle(cx, cy, radius, "S");
  if (filled > 0) {
    doc.setDrawColor(AMBER[0], AMBER[1], AMBER[2]);
    doc.setLineWidth(4);
    for (let i = 0; i < filled; i++) {
      const angle1 = (i / segments) * 2 * Math.PI - Math.PI / 2;
      const angle2 = ((i + 1) / segments) * 2 * Math.PI - Math.PI / 2;
      doc.line(
        cx + radius * Math.cos(angle1), cy + radius * Math.sin(angle1),
        cx + radius * Math.cos(angle2), cy + radius * Math.sin(angle2)
      );
    }
  }
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text(`${pct}%`, cx, cy + 2, { align: "center" });
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(GREY[0], GREY[1], GREY[2]);
  doc.text("complété", cx, cy + 7, { align: "center" });
}

function drawCategoryBar(doc: jsPDF, x: number, y: number, w: number, categories: {name: string; count: number; color: number[]}[]) {
  const total = categories.reduce((s, c) => s + c.count, 0);
  if (total === 0) return;
  let currentX = x;
  categories.forEach((cat, i) => {
    const barW = (cat.count / total) * w;
    const r = i === 0 && i === categories.length - 1 ? 2 : i === 0 ? 2 : i === categories.length - 1 ? 2 : 0;
    doc.setFillColor(cat.color[0], cat.color[1], cat.color[2]);
    doc.roundedRect(currentX, y, barW, 5, r, r, "F");
    currentX += barW;
  });
}

function addHeaderImage(doc: jsPDF, headerImage: string, pageW: number) {
  // Add the image as a full-width banner at top of page
  try {
    const imgFormat = headerImage.includes("image/png") ? "PNG" : "JPEG";
    doc.addImage(headerImage, imgFormat, 0, 0, pageW, 55, undefined, "FAST");
    // Add dark gradient overlay for text readability
    doc.setGState(new (doc as any).GState({ opacity: 0.6 }));
    doc.setFillColor(20, 20, 25);
    doc.rect(0, 0, pageW, 55, "F");
    doc.setGState(new (doc as any).GState({ opacity: 1 }));
  } catch {
    // Fallback: just use dark header
    doc.setFillColor(DARK[0], DARK[1], DARK[2]);
    doc.rect(0, 0, pageW, 55, "F");
  }
}

function addPageBanner(doc: jsPDF, headerImage: string | undefined, pageW: number, title: string) {
  if (headerImage) {
    try {
      const imgFormat = headerImage.includes("image/png") ? "PNG" : "JPEG";
      doc.addImage(headerImage, imgFormat, 0, 0, pageW, 40, undefined, "FAST");
      doc.setGState(new (doc as any).GState({ opacity: 0.65 }));
      doc.setFillColor(20, 20, 25);
      doc.rect(0, 0, pageW, 40, "F");
      doc.setGState(new (doc as any).GState({ opacity: 1 }));
    } catch {
      doc.setFillColor(DARK[0], DARK[1], DARK[2]);
      doc.rect(0, 0, pageW, 40, "F");
    }
  } else {
    doc.setFillColor(DARK[0], DARK[1], DARK[2]);
    doc.rect(0, 0, pageW, 40, "F");
  }
  // Amber accent line
  doc.setFillColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.rect(0, 40, pageW, 1.5, "F");
  // Title on banner
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text(title, 18, 25);
  // Grafton branding
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.text("GRAFTON IA", 18, 33);
}

export function generateActionPlanPdf({ projectName, actions, doneCount, progress, headerImage }: ActionPlanPdfOptions): Blob {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const pageH = 297;
  const margin = 18;
  const contentW = pageW - margin * 2;

  // ── PAGE 1: COVER ──
  
  // Full cover image or dark header
  if (headerImage) {
    addHeaderImage(doc, headerImage, pageW);
    // Extend dark area below image
    doc.setFillColor(DARK[0], DARK[1], DARK[2]);
    doc.rect(0, 55, pageW, 42.5, "F");
  } else {
    doc.setFillColor(DARK[0], DARK[1], DARK[2]);
    doc.rect(0, 0, pageW, 97.5, "F");
  }

  // Amber accent line
  doc.setFillColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.rect(0, 97.5, pageW, 2.5, "F");

  // Decorative geometric shapes
  doc.setFillColor(45, 45, 50);
  doc.circle(pageW - 25, 20, 30, "F");
  doc.setFillColor(50, 50, 55);
  doc.circle(pageW - 10, 55, 15, "F");

  // Logo
  doc.setFillColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.roundedRect(margin, 60, 10, 10, 2, 2, "F");
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text("G", margin + 3.5, 67);

  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("GRAFTON", margin + 13, 66);
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.text("INTELLIGENCE ARTIFICIELLE", margin + 13, 70);

  // Title
  doc.setFontSize(28);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(255, 255, 255);
  doc.text("Plan d'Actions", margin, 82);
  doc.setFontSize(26);
  doc.text("Stratégique", margin, 93);

  // Date & project
  const now = new Date();
  const dateStr = now.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  
  // Project name card
  doc.setFillColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.roundedRect(margin, 108, contentW, 18, 3, 3, "F");
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text(projectName || "Votre entreprise", margin + 8, 119);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.text(`Généré le ${dateStr}`, margin + 8, 123);

  // ── KPI SUMMARY CARDS ──
  let y = 136;
  const cardW = (contentW - 8) / 3;
  const cards = [
    { label: "Actions totales", value: String(actions.length), sub: "identifiées par l'IA", color: BLUE },
    { label: "Complétées", value: String(doneCount), sub: `sur ${actions.length}`, color: GREEN },
    { label: "Priorité haute", value: String(actions.filter(a => a.priority === "haute").length), sub: "actions urgentes", color: RED },
  ];

  cards.forEach((card, i) => {
    const cx = margin + i * (cardW + 4);
    drawRoundedRect(doc, cx, y, cardW, 32, 3, CARD_BG, LIGHT_GREY);
    doc.setFillColor(card.color[0], card.color[1], card.color[2]);
    doc.roundedRect(cx, y, cardW, 2, 1, 1, "F");
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(DARK[0], DARK[1], DARK[2]);
    doc.text(card.value, cx + 8, y + 16);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(GREY[0], GREY[1], GREY[2]);
    doc.text(card.label.toUpperCase(), cx + 8, y + 22);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(160, 160, 165);
    doc.text(card.sub, cx + 8, y + 27);
  });

  y += 42;

  // ── PROGRESS SECTION ──
  drawRoundedRect(doc, margin, y, contentW, 36, 3, [250, 250, 253], LIGHT_GREY);
  drawDonutChart(doc, margin + 22, y + 18, 11, progress);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(DARK[0], DARK[1], DARK[2]);
  doc.text("Avancement global", margin + 42, y + 10);
  drawProgressBar(doc, margin + 42, y + 14, contentW - 52, 4, progress);
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(GREY[0], GREY[1], GREY[2]);
  doc.text(`${doneCount} actions sur ${actions.length} ont été complétées.`, margin + 42, y + 25);

  const categoryMap = new Map<string, number>();
  actions.forEach(a => categoryMap.set(a.category, (categoryMap.get(a.category) || 0) + 1));
  const catColors = [AMBER, BLUE, GREEN, RED, [160, 100, 200], [100, 180, 200], [200, 100, 150]];
  const categoriesArr = [...categoryMap.entries()].map(([name, count], i) => ({
    name, count, color: catColors[i % catColors.length]
  }));
  drawCategoryBar(doc, margin + 42, y + 29, contentW - 52, categoriesArr);

  y += 44;

  // Category legend
  doc.setFontSize(6);
  let legendX = margin;
  categoriesArr.forEach((cat) => {
    doc.setFillColor(cat.color[0], cat.color[1], cat.color[2]);
    doc.circle(legendX + 2, y + 2, 1.5, "F");
    doc.setTextColor(GREY[0], GREY[1], GREY[2]);
    doc.text(`${cat.name} (${cat.count})`, legendX + 5, y + 3);
    legendX += doc.getTextWidth(`${cat.name} (${cat.count})`) + 10;
  });

  // ── PAGE 2+: DETAILED ACTIONS ──
  doc.addPage();
  addPageBanner(doc, headerImage, pageW, "Détail des actions prioritaires");
  y = 50;

  actions.forEach((action, i) => {
    if (y > pageH - 35) {
      doc.addPage();
      addPageBanner(doc, headerImage, pageW, "Détail des actions (suite)");
      y = 50;
    }

    const rowH = 22;
    const bgColor = action.done ? [240, 255, 245] : (i % 2 === 0 ? CARD_BG : WHITE);
    drawRoundedRect(doc, margin, y, contentW, rowH, 2, bgColor as number[]);

    // Number badge
    const numBg = action.priority === "haute" ? RED : action.priority === "moyenne" ? AMBER : GREEN;
    doc.setFillColor(numBg[0], numBg[1], numBg[2]);
    doc.roundedRect(margin + 4, y + 3, 7, 7, 2, 2, "F");
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(String(i + 1), margin + 6, y + 8, { align: "center" });

    // Status
    if (action.done) {
      doc.setFontSize(10);
      doc.setTextColor(GREEN[0], GREEN[1], GREEN[2]);
      doc.text("✓", margin + 15, y + 9);
    } else {
      doc.setDrawColor(LIGHT_GREY[0], LIGHT_GREY[1], LIGHT_GREY[2]);
      doc.setLineWidth(0.4);
      doc.circle(margin + 15, y + 7, 2.5, "S");
    }

    // Label
    doc.setFontSize(9);
    doc.setFont("helvetica", action.done ? "normal" : "bold");
    doc.setTextColor(action.done ? GREY[0] : DARK[0], action.done ? GREY[1] : DARK[1], action.done ? GREY[2] : DARK[2]);
    const labelLines = doc.splitTextToSize(action.label, contentW - 55);
    doc.text(labelLines[0], margin + 22, y + 8);

    // Tags
    doc.setFontSize(6.5);
    doc.setFont("helvetica", "normal");
    const prioColors: Record<string, number[]> = { haute: [255, 230, 230], moyenne: [255, 245, 220], basse: [225, 245, 225] };
    const prioTextColors: Record<string, number[]> = { haute: RED, moyenne: AMBER, basse: GREEN };
    const prioBg = prioColors[action.priority] || CARD_BG;
    const prioText = prioTextColors[action.priority] || GREY;
    const prioW = doc.getTextWidth(action.priority.toUpperCase()) + 5;
    doc.setFillColor(prioBg[0], prioBg[1], prioBg[2]);
    doc.roundedRect(margin + 22, y + 12, prioW, 5, 1.5, 1.5, "F");
    doc.setTextColor(prioText[0], prioText[1], prioText[2]);
    doc.text(action.priority.toUpperCase(), margin + 24.5, y + 15.5);

    const catW = doc.getTextWidth(action.category) + 5;
    doc.setFillColor(235, 235, 240);
    doc.roundedRect(margin + 22 + prioW + 3, y + 12, catW, 5, 1.5, 1.5, "F");
    doc.setTextColor(GREY[0], GREY[1], GREY[2]);
    doc.text(action.category, margin + 24.5 + prioW + 3, y + 15.5);

    y += rowH + 3;
  });

  // ── FOOTER ──
  y += 10;
  if (y > pageH - 40) {
    doc.addPage();
    y = margin;
  }
  doc.setDrawColor(LIGHT_GREY[0], LIGHT_GREY[1], LIGHT_GREY[2]);
  doc.setLineWidth(0.2);
  doc.line(margin, y, pageW - margin, y);
  y += 8;
  doc.setFontSize(7);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(160, 160, 165);
  doc.text("Ce plan d'actions a été généré par Grafton IA — Intelligence Artificielle dédiée aux dirigeants.", margin, y);
  doc.text("Document confidentiel. Toute reproduction est interdite sans autorisation.", margin, y + 5);
  doc.text(`© ${now.getFullYear()} Grafton — grafton.ai`, margin, y + 10);

  // Page numbers
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 180, 185);
    doc.text(`${p} / ${totalPages}`, pageW - margin, pageH - 8, { align: "right" });
  }

  return doc.output("blob");
}
